import React, { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { BiPurchaseTag, BiReceipt } from "react-icons/bi";
import { MdOutlineDiscount, MdLocalShipping, MdPayments } from "react-icons/md";
import backendVerifiedUser from "../../api/backendVerifiedUser";
import DashboardTiles from "../Dashboard/DashboardTiles";
import ReportsChart from "./ReportsChart";
import ReportsDateFilter from "./ReportsDateFilter";
import { computeDateRange } from "./reportFilters";

export default function SalesReportTab() {
  const today = useMemo(() => new Date(), []);
  const [rangeMode, setRangeMode] = useState("month");
  const [singleDate, setSingleDate] = useState(today);
  const [dateRange, setDateRange] = useState([
    new Date(today.getFullYear(), today.getMonth(), 1),
    today,
  ]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const { startDate, endDate } = useMemo(
    () => computeDateRange(rangeMode, today, singleDate, dateRange),
    [rangeMode, singleDate, dateRange, today]
  );

  useEffect(() => {
    let cancelled = false;
    const fetchReport = async () => {
      setLoading(true);
      try {
        const params = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;

        const response = await backendVerifiedUser.get("/reports/sales", { params });
        if (!cancelled) setReport(response.data);
      } catch (err) {
        console.log(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchReport();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const summary = report?.summary || {};

  const kpiData = [
    { amount: summary.totalOrders || 0, title: "Total Orders", icon: <BiReceipt /> },
    { amount: summary.grossSales || 0, title: "Gross Sales", icon: <BiPurchaseTag />, prefix: "RS" },
    { amount: summary.totalDiscount || 0, title: "Total Discount", icon: <MdOutlineDiscount />, prefix: "RS" },
    { amount: summary.totalShipping || 0, title: "Total Shipping", icon: <MdLocalShipping />, prefix: "RS" },
    { amount: summary.netSales || 0, title: "Net Sales", icon: <BiPurchaseTag />, prefix: "RS" },
    { amount: Math.round(summary.avgOrderValue || 0), title: "Avg Order Value", icon: <MdPayments />, prefix: "RS" },
  ];

  const handleExport = () => {
    if (!report) return;

    const summaryRows = [
      ["Metric", "Value"],
      ["Date Range", `${startDate || "-"} to ${endDate || "-"}`],
      ["Total Orders", summary.totalOrders || 0],
      ["Gross Sales", summary.grossSales || 0],
      ["Total Discount", summary.totalDiscount || 0],
      ["Total Shipping", summary.totalShipping || 0],
      ["Net Sales", summary.netSales || 0],
      ["Avg Order Value", Math.round(summary.avgOrderValue || 0)],
      ["Paid Orders", summary.paidOrders || 0],
      ["Unpaid Orders", summary.unpaidOrders || 0],
    ];

    const paymentRows = [
      ["Payment Method", "Orders", "Sales"],
      ...(report.byPaymentMethod || []).map((p) => [p.method, p.orders, p.sales]),
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), "Summary");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(paymentRows), "By Payment Method");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `sales-report-${startDate || "all"}-to-${endDate || "all"}.xlsx`);
  };

  return (
    <div>
      <div className="flex items-center justify-end mb-3">
        <Button
          label="Export to Excel"
          icon="pi pi-file-excel"
          className="p-primary-btn"
          onClick={handleExport}
          disabled={!report}
        />
      </div>

      <ReportsDateFilter
        rangeMode={rangeMode}
        setRangeMode={setRangeMode}
        singleDate={singleDate}
        setSingleDate={setSingleDate}
        dateRange={dateRange}
        setDateRange={setDateRange}
        loading={loading}
      />

      {report && (
        <>
          <div className="mb-3 font-semibold text-black/50 text-lg ml-1">Summary</div>
          <DashboardTiles data={kpiData} />

          <div className="mt-8">
            <div className="my-3 font-semibold text-black/50 text-xl ml-1">Sales Trend</div>
            <div className="card shadow-md p-4">
              {report.chartData && report.chartData.length > 0 ? (
                <ReportsChart data={report.chartData} />
              ) : (
                <div className="text-gray-400 text-sm py-8 text-center">No data for the selected filters.</div>
              )}
            </div>
          </div>

          <div className="mt-8">
            <div className="my-3 font-semibold text-black/50 text-xl ml-1">Sales by Payment Method</div>
            <div className="card shadow-md">
              <DataTable value={report.byPaymentMethod || []} stripedRows size="small">
                <Column field="method" header="Payment Method" />
                <Column field="orders" header="Orders" />
                <Column field="sales" header="Sales (RS)" body={(row) => `RS ${Number(row.sales || 0).toLocaleString("en-IN")}`} />
              </DataTable>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
